import { esc } from '../ui/modal';
import { t } from '../ui/lang';
import { farmInstallCmd } from './farm-docker-config';
import { downloadMd } from './farm-setup-shared';
/**
 * Docker recipe of the AdsPower web-reader agent (docker/Dockerfile.adspower-agent). The
 * agent runs on the OPERATOR's machine next to the AdsPower desktop app, so the Setup tab
 * hands out a plain `docker run` per platform — no repo checkout, no compose file. Like a
 * host farm, the recipe also writes a host-side mf-update.sh and mounts the docker socket so
 * the panel's "Restart with update" works (web_reader/self_update.py runs that script in a
 * helper container, as farm-docker-config.ts does for host farms).
 */
export const ADSPOWER_AGENT_IMAGE = 'alkhrameev/adspower-agent:latest';
export const ADSPOWER_AGENT_CONTAINER = 'adspower-agent';
const DOCKER_SOCK = '/var/run/docker.sock';
const UPDATE_SCRIPT = '.adspower-agent/mf-update.sh';
const SCRIPT_HOST_MARK = '@@SCRIPT_HOST@@';
/** Where the docker DAEMON finds mf-update.sh. Linux and macOS: $HOME (Docker Desktop shares
 *  /Users). Windows: the daemon runs in Docker Desktop's VM, which sees drive C: under
 *  /run/desktop/mnt/host/c — this PowerShell expression maps $HOME there. */
const PS_SCRIPT_HOST = `/run/desktop/mnt/host/$($HOME.Substring(0,1).ToLower())$($HOME.Substring(2) -replace '\\\\','/')/${UPDATE_SCRIPT}`;
const SH_SCRIPT_HOST = `$HOME/${UPDATE_SCRIPT}`;
/** Windows runs the agent from PowerShell: backtick continuations, no `|| true`. */
function cleanupCmd(platform) {
    if (platform === 'windows') {
        return `docker rm -f ${ADSPOWER_AGENT_CONTAINER}
docker rmi ${ADSPOWER_AGENT_IMAGE}
docker image prune -f`;
    }
    return `docker stop ${ADSPOWER_AGENT_CONTAINER} || true
docker rm   ${ADSPOWER_AGENT_CONTAINER} || true
docker rmi  ${ADSPOWER_AGENT_IMAGE} || true
docker image prune -f || true`;
}
/**
 * AdsPower binds its Local API and the profiles' CDP ports to 127.0.0.1. On Linux the
 * container shares the host network, so loopback is the host's. Docker Desktop reaches
 * the host through host.docker.internal instead (ADSPOWER_HOST swaps the host part of
 * the farm's Local API URL), and the seen-deposit cache lives in a named volume.
 */
function runRecipe(platform, opts) {
    const net = platform === 'linux'
        ? ['--network host', '-v /var/lib/adspower-agent-data:/data', '-e ADSPOWER_HOST=127.0.0.1']
        : ['--add-host host.docker.internal:host-gateway', '-v adspower-agent-data:/data', '-e ADSPOWER_HOST=host.docker.internal'];
    // Google ships chromedriver for linux amd64 only, so the image is amd64-only; an
    // Apple-silicon Mac runs it through Docker Desktop's Rosetta emulation.
    const plat = platform === 'mac' ? ['--platform linux/amd64'] : [];
    const lines = [
        'docker run -d',
        `--name ${ADSPOWER_AGENT_CONTAINER}`,
        '--restart unless-stopped',
        ...plat,
        ...net,
        `-v ${DOCKER_SOCK}:${DOCKER_SOCK}`,
        opts.panelUrlArg,
        `-e FARM_KEY=${opts.key}`,
        // Like the host farm's NODE_TLS_REJECT_UNAUTHORIZED=0: the panel is on a self-signed cert.
        '-e PANEL_TLS_VERIFY=0',
        '-e DRY_RUN=0',
        opts.scriptHostArg,
        `-e FARM_UPDATE_IMAGE=${ADSPOWER_AGENT_IMAGE}`,
        ADSPOWER_AGENT_IMAGE,
    ];
    return lines.map((l, i) => `${i ? '  ' : ''}${l}${i < lines.length - 1 ? opts.cont : ''}`).join('\n');
}
/** The launch step, in the platform's own shell. */
function dockerRunCmd(platform, panelUrl, key) {
    return runRecipe(platform, {
        cont: platform === 'windows' ? ' `' : ' \\',
        panelUrlArg: `-e PANEL_URL=${panelUrl}`,
        scriptHostArg: platform === 'windows'
            ? `-e "FARM_UPDATE_SCRIPT_HOST=${PS_SCRIPT_HOST}"`
            : `-e FARM_UPDATE_SCRIPT_HOST=${SH_SCRIPT_HOST}`,
        key,
    });
}
/**
 * mf-update.sh as the helper container runs it (POSIX sh, whatever the host OS): pull, drop
 * the container, re-run the launch recipe. Mirrors farmCreateUpdaterCmd for host farms; the
 * daemon-side path of the script itself is left as SCRIPT_HOST_MARK for the writer to fill.
 */
function updateScript(platform, panelUrl, key) {
    const pullPlat = platform === 'mac' ? ' --platform linux/amd64' : '';
    const recipe = runRecipe(platform, {
        cont: ' \\', panelUrlArg: '-e PANEL_URL="$PANEL_URL_EFF"',
        scriptHostArg: `-e FARM_UPDATE_SCRIPT_HOST=${SCRIPT_HOST_MARK}`, key,
    });
    return `#!/bin/sh
# mf-update.sh — host-side restart/update for the adspower-agent container.
# Default: pull then recreate (UPDATE). --no-pull: recreate same image (RESTART).
set -e
PULL=1
for a in "$@"; do
    case "$a" in
        --no-pull) PULL=0 ;;
        *) echo "mf-update: unknown arg: $a" >&2; exit 2 ;;
    esac
done
IMAGE=${ADSPOWER_AGENT_IMAGE}
if [ "$PULL" = 1 ]; then
    echo "mf-update: pulling $IMAGE"
    docker pull${pullPlat} "$IMAGE"
else
    echo "mf-update: --no-pull, recreating with local image $IMAGE"
fi
docker rm -f ${ADSPOWER_AGENT_CONTAINER} 2>/dev/null || true
PANEL_URL_EFF="\${MF_RELAY_HOST:-${panelUrl}}"
${recipe}
docker image prune -f || true`;
}
/**
 * Writes mf-update.sh on the host BEFORE the launch step, so FARM_UPDATE_SCRIPT_HOST points at
 * a real file. bash: an unquoted heredoc (so $HOME expands into the file) with every runtime
 * `$` and line-continuation backslash escaped. PowerShell: a literal here-string, LF endings
 * (sh chokes on CRLF), the Docker Desktop VM path of the script substituted in.
 */
function updaterCmd(platform, panelUrl, key) {
    const body = updateScript(platform, panelUrl, key);
    if (platform === 'windows') {
        return `New-Item -ItemType Directory -Force "$HOME\\.adspower-agent" | Out-Null
$mf = @'
${body}
'@
$mf = $mf.Replace('${SCRIPT_HOST_MARK}', "${PS_SCRIPT_HOST}").Replace("\`r\`n", "\`n")
[IO.File]::WriteAllText("$HOME\\.adspower-agent\\mf-update.sh", $mf + "\`n")`;
    }
    const escaped = body
        .replace(/\\/g, '\\\\')
        .replace(/\$/g, '\\$')
        .split(SCRIPT_HOST_MARK).join(SH_SCRIPT_HOST);
    return `mkdir -p "$HOME/.adspower-agent"
cat > "$HOME/${UPDATE_SCRIPT}" <<MFEOF
${escaped}
MFEOF
chmod 600 "$HOME/${UPDATE_SCRIPT}"`;
}
/** Fallback when Docker Desktop cannot reach AdsPower's loopback ports: run from the repo. */
function nativeCmd(platform, panelUrl, key) {
    if (platform === 'windows') {
        return `cd mobile-clicker
pip install -r web_reader\\requirements.txt
$env:PYTHONPATH=".;duo"; $env:PANEL_URL="${panelUrl}"; $env:FARM_KEY="${key}"; $env:PANEL_TLS_VERIFY="0"
python -m web_reader.run`;
    }
    return `cd mobile-clicker
pip install -r web_reader/requirements.txt
PYTHONPATH=.:duo PANEL_URL=${panelUrl} FARM_KEY=${key} PANEL_TLS_VERIFY=0 python -m web_reader.run`;
}
const INSTALL_CMD = {
    linux: farmInstallCmd(),
    windows: 'winget install --exact Docker.DockerDesktop',
    mac: 'brew install --cask docker',
};
const MD_TITLE = { linux: 'Linux', windows: 'Windows', mac: 'macOS' };
const SHELL = { linux: 'bash', windows: 'powershell', mac: 'bash' };
export function buildAdspowerSetup(name, key, platform) {
    const panelUrl = window.location.origin;
    const cmds = {
        install: INSTALL_CMD[platform],
        updater: updaterCmd(platform, panelUrl, key),
        cleanup: cleanupCmd(platform),
        run: dockerRunCmd(platform, panelUrl, key),
    };
    if (platform !== 'linux')
        cmds['native'] = nativeCmd(platform, panelUrl, key);
    const block = (cmd) => `\`\`\`${SHELL[platform]}\n${cmd}\n\`\`\``;
    const desktop = platform !== 'linux';
    let n = 0;
    const step = (title) => `## ${++n}. ${title}`;
    const mdContent = `# AdsPower web-reader setup (${MD_TITLE[platform]}) — ${name}

The agent runs on the machine with the AdsPower desktop app: on **Start** it opens the
profile through AdsPower's Local API, works in the bank tab the operator keeps open in that profile (it never opens the page itself), and
the web flow logs in with the device's PIN / app password and reads deposits.
${desktop ? `
Docker Desktop reaches the host through \`host.docker.internal\`. AdsPower binds its Local
API and the profiles' CDP ports to \`127.0.0.1\`, so the container only sees them when
AdsPower listens beyond loopback or Docker Desktop's host networking (4.34+) is on —
otherwise run the agent natively (last step).
` : ''}
${step(desktop ? 'Install Docker Desktop' : 'Install Docker (Ubuntu / Debian)')}
${desktop ? '\nStart Docker Desktop once and turn on **Start Docker Desktop when you sign in**.\n' : ''}
${block(cmds['install'])}

${step('Enable the AdsPower Local API')}

In the AdsPower app open **Settings → Local API** and turn it on. Put its URL
(\`http://127.0.0.1:50325\` by default) and API key into this farm's **General** tab —
the agent fetches them from the panel together with its config.

${step('Create the self-update helper')}

Installs the host-side updater the panel's **Restart with update** (farm → General) runs.

${block(cmds['updater'])}

${step('Stop and remove the existing container and image')}

For a manual update of an already-running agent start here, then re-run the next step
(\`docker rmi\` removes the local image, so \`docker run\` auto-pulls the latest).

${block(cmds['cleanup'])}

${step('Start the container')}

${block(cmds['run'])}

> Seen-deposit keys, the ops spool and the chromedriver cache live in
> ${platform === 'linux' ? '`/var/lib/adspower-agent-data`' : 'the `adspower-agent-data` volume'}
> and survive container recreation. \`DRY_RUN=1\` reads and logs deposits without sending
> them to the platform.

${step('Set up the profiles in the panel')}

Open each profile's clicker settings (gear) on this farm:

- Assign a **web** deposit flow (task = deposit).
- Set the **bank URL**, the **PIN** and the **app password** the flow logs in with.
- Press **Start** — the agent runs the flow for every started profile on the farm's cycle interval.
${desktop ? `
${step('Alternative: run the agent natively')}

If the container cannot reach the Local API, run the agent next to AdsPower from a checkout
of the repo (Python 3.11+):

${block(cmds['native'])}
` : ''}`;
    return { platform, cmds, mdContent };
}
/** Steps in order; `copy` names the command shown under the step (text-only steps have none). */
function steps(platform) {
    const install = platform === 'linux' ? 'farms.adspower_step_install' : 'farms.adspower_step_install_desktop';
    return [
        { text: install, copy: 'install' },
        { text: 'farms.adspower_step_api' },
        { text: 'farms.adspower_step_updater', copy: 'updater' },
        { text: 'farms.adspower_step_cleanup', copy: 'cleanup' },
        { text: 'farms.adspower_step_run', copy: 'run' },
    ];
}
/** Inner HTML for one platform sub-tab; copy/download wired by {@link wireAdspowerSetup}. */
export function adspowerSetupHtml(s) {
    const desktop = s.platform !== 'linux';
    let n = 0;
    const stepHtml = (st) => `
        <p class="modal__text"${n ? ' style="margin-top:12px"' : ''}>${t(st.text, { n: ++n })}</p>
        ${st.copy ? `<div class="launch-block">
            <pre class="launch-pre">${esc(s.cmds[st.copy] ?? '')}</pre>
            <button class="tbl-btn copy-key-btn" data-ap-copy="${st.copy}" title="${t('common.copy')}">⎘ ${t('common.copy')}</button>
        </div>` : ''}`;
    const dataNote = s.platform === 'linux' ? '/var/lib/adspower-agent-data' : 'adspower-agent-data';
    return `
        <p class="modal__text" style="margin-bottom:12px">${t('farms.adspower_intro')}</p>
        ${desktop ? `<p class="modal__text" style="margin-bottom:12px">${t('farms.adspower_desktop_intro')}</p>` : ''}
        ${steps(s.platform).map(stepHtml).join('')}
        <p class="modal__text" style="margin-top:6px;font-size:0.85em;opacity:0.7">
            ${t('farms.adspower_data_note', { path: dataNote })}
        </p>
        <p class="modal__text" style="margin-top:12px">${t('farms.adspower_step_profiles', { n: ++n })}</p>
        <ul class="modal__text" style="margin:6px 0 0 18px;line-height:1.7">
            <li>${t('farms.adspower_li1')}</li>
            <li>${t('farms.adspower_li2')}</li>
            <li>${t('farms.adspower_li3')}</li>
        </ul>
        ${desktop ? `
        <p class="modal__text" style="margin-top:12px">${t('farms.adspower_step_native', { n: ++n })}</p>
        <div class="launch-block">
            <pre class="launch-pre">${esc(s.cmds['native'] ?? '')}</pre>
            <button class="tbl-btn copy-key-btn" data-ap-copy="native" title="${t('common.copy')}">⎘ ${t('common.copy')}</button>
        </div>` : ''}
        <div class="modal__actions">
            <button type="button" class="tbl-btn" data-ap-download style="margin-right:auto">${t('farms.download_md')}</button>
        </div>
    `;
}
export function wireAdspowerSetup(root, s, name) {
    root.querySelectorAll('[data-ap-copy]').forEach((btn) => {
        btn.addEventListener('click', () => {
            const cmd = s.cmds[btn.dataset['apCopy'] ?? ''];
            if (cmd === undefined)
                return;
            void navigator.clipboard.writeText(cmd);
            btn.textContent = `✓ ${t('common.copied')}`;
            setTimeout(() => { btn.textContent = `⎘ ${t('common.copy')}`; }, 1500);
        });
    });
    root.querySelector('[data-ap-download]')?.addEventListener('click', () => {
        downloadMd(s.mdContent, `adspower-setup-${s.platform}-${name}`);
    });
}
const PLATFORMS = [
    { key: 'linux', label: 'farms.subtab_linux' },
    { key: 'windows', label: 'farms.subtab_windows' },
    { key: 'mac', label: 'farms.subtab_mac' },
];
/**
 * Renders the AdsPower Setup body (Linux / Windows / macOS sub-tabs) into `host`, the
 * same sub-tab switcher the host farms' Launch / Setup tab uses. Default is Linux.
 */
export function renderAdspowerSetupBody(host, name, key) {
    const setups = PLATFORMS.map((p) => buildAdspowerSetup(name, key, p.key));
    host.innerHTML = `
        <h3 class="settings-section__title">${t('farms.adspower_setup_title')}</h3>
        <div class="launch-subtabs" role="tablist">
            ${PLATFORMS.map((p, i) => `<button type="button" class="launch-subtab${i ? '' : ' active'}" data-subtab="${p.key}">${t(p.label)}</button>`).join('')}
        </div>
        ${setups.map((s, i) => `<div class="launch-subpanel" data-subpanel="${s.platform}"${i ? ' style="display:none"' : ''}>${adspowerSetupHtml(s)}</div>`).join('')}
    `;
    setups.forEach((s) => {
        wireAdspowerSetup(host.querySelector(`[data-subpanel="${s.platform}"]`), s, name);
    });
    const tabs = Array.from(host.querySelectorAll('.launch-subtab'));
    const panels = Array.from(host.querySelectorAll('.launch-subpanel'));
    tabs.forEach((tab) => tab.addEventListener('click', () => {
        const k = tab.dataset['subtab'];
        tabs.forEach((b) => b.classList.toggle('active', b.dataset['subtab'] === k));
        panels.forEach((p) => { p.style.display = p.dataset['subpanel'] === k ? '' : 'none'; });
    }));
}
